import React from "react";
import { Modal, Button } from "react-bootstrap";
import { FaTimes, FaUser, FaInfoCircle, FaStickyNote } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import classes from "./DischargeModal.module.css";

const InfoRow = ({ label, value, color = "text-muted" }) => (
    <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom border-light">
        <span className={`${color} small`}>{label}</span>
        <span className="fw-bold text-dark small text-end">{value}</span>
    </div>
);

const AdmissionStatusNotesModal = ({ show, onHide, admission }) => {
    const { t } = useTranslation();
    if (!admission) return null;

    const calculateAge = (dob) => {
        if (!dob) return "";
        const birthDate = new Date(dob);
        if (isNaN(birthDate.getTime())) return "";
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const patientName = `${admission.patientId?.firstName || ""} ${admission.patientId?.lastName || ""}`;
    const age = calculateAge(admission.patientId?.dob) || admission.patientId?.age || "-";
    const gender = admission.patientId?.gender || "-";

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable className="shadow-premium">
            <div className="modal-content border-0 overflow-hidden rounded-4">
                <div className="modal-header bg-premium text-white py-3">
                    <h5 className="modal-title d-flex align-items-center">
                        <FaInfoCircle className="me-2" /> {t("Admission Details")} - {t(admission.status)}
                    </h5>
                    <button type="button" className="btn-close btn-close-white" onClick={onHide} aria-label={t("Close")}></button>
                </div>

                <Modal.Body className="p-4 bg-light">
                    <div className="row g-4">
                        {/* Patient Profile Summary */}
                        <div className="col-md-5">
                            <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                                <div className="card-header bg-premium text-white py-2">
                                    <h6 className="mb-0 fw-bold small"><FaUser className="me-2" /> {t("Patient Info")}</h6>
                                </div>
                                <div className="card-body p-3">
                                    <div className="text-center mb-3">
                                        <div className="avatar-circle mx-auto mb-2 bg-premium text-white d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{ width: '60px', height: '60px', borderRadius: '50%', fontSize: '24px' }}>
                                            {admission.patientId?.firstName?.charAt(0)}{admission.patientId?.lastName?.charAt(0)}
                                        </div>
                                        <h6 className="mb-0 fw-bold text-dark">{patientName}</h6>
                                        <span className="text-muted small">{t("PID")}: {admission.patientId?.patientId}</span>
                                    </div>
                                    <div className="mt-3">
                                        <InfoRow label={t("IPD Number")} value={admission.ipdAdmissionId} />
                                        <InfoRow label={t("Gender / Age")} value={`${t(gender)} / ${age} ${t("Yrs")}`} />
                                        <InfoRow label={t("Mobile")} value={admission.patientId?.mobile || '-'} color="text-teal" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status & Notes */}
                        <div className="col-md-7">
                            <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                                <div className="card-header bg-premium text-white py-2">
                                    <h6 className="mb-0 fw-bold small"><FaStickyNote className="me-2" /> {t("Status Details")}</h6>
                                </div>
                                <div className="card-body p-3">
                                    <div className="mb-3">
                                        <label className="text-muted small d-block mb-1">{t("Current Status")}</label>
                                        <div className={`p-2 rounded text-center fw-bold ${admission.status === 'CANCELLED' ? 'bg-danger text-white' : 'bg-teal text-white'}`}>
                                            {t(admission.status)}
                                        </div>
                                    </div>

                                    {admission.option2 && (
                                        <div className="mb-3">
                                            <label className="text-muted small d-block mb-1">{t("Reason / Detail")}</label>
                                            <div className="p-2 bg-white rounded border-start border-4 border-danger small text-dark">
                                                {admission.option2}
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-muted small d-block mb-1">{t("Notes / Remarks")}</label>
                                        <div className="p-3 bg-white rounded border border-light small text-dark" style={{ minHeight: "100px", whiteSpace: "pre-wrap", borderLeft: "4px solid var(--primary-color)" }}>
                                            {admission.notes || <span className="text-muted italic">{t("No notes provided for this status change.")}</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="bg-white p-3 border-top">
                    <Button className="btn-secondary-premium shadow-sm px-5" onClick={onHide}>
                        <FaTimes className="me-2" /> {t("CLOSE")}
                    </Button>
                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default AdmissionStatusNotesModal;
